// Enhanced SQL autocomplete provider for Monaco Editor
import * as monaco from 'monaco-editor';
import { SQL_KEYWORDS } from '@/utils/sqlKeywords';
import { AgentProxyService } from '@/services/agentProxyService';

export interface SqlCompletionContext {
  tables: string[];
  columns: { tableName: string; columns: { name?: string; column_name?: string }[] }[];
  schemas: string[];
  functions: (string | { name?: string; returnType?: string; language?: string; parameters?: { name?: string; type?: string; mode?: 'IN' | 'OUT' | 'INOUT' }[]; schema?: string })[];
  datasourceId?: string;
  databaseName?: string;
  schemaName?: string;
  databaseType?: string;
}

export class SqlCompletionProvider implements monaco.languages.CompletionItemProvider {
  public context: SqlCompletionContext;
  private columnCache = new Map<string, { name?: string; column_name?: string }[]>();
  private fetchingColumns = new Set<string>();
  
  // Define trigger characters that should activate autocomplete
  public triggerCharacters = ['.', ' ', '(', ',', '=', '<', '>', '!'];

  constructor(context: SqlCompletionContext) {
    this.context = context;
  }

  updateContext(context: SqlCompletionContext) {
    this.context = context;
  }

  // Dynamically fetch columns for a table
  private async fetchColumnsForTable(tableName: string): Promise<{ name?: string; column_name?: string }[]> {
    if (!this.context.datasourceId || !this.context.databaseName) {
      console.warn('Cannot fetch columns: missing datasourceId or databaseName');
      return [];
    }

    const cacheKey = `${this.context.datasourceId}-${this.context.databaseName}-${tableName}`;
    
    // Return cached columns if available
    if (this.columnCache.has(cacheKey)) {
      return this.columnCache.get(cacheKey) || [];
    }

    // Avoid duplicate fetches
    if (this.fetchingColumns.has(cacheKey)) {
      console.log(`Already fetching columns for ${tableName}, skipping duplicate request`);
      return [];
    }

    this.fetchingColumns.add(cacheKey);
    console.log(`Fetching columns for table: ${tableName}`);

    try {
      // Determine schema parameter for PostgreSQL
      const schemaName = this.context.databaseType === 'PostgreSQL' ? this.context.schemaName : undefined;
      
      const response = await AgentProxyService.getColumnInfo(
        this.context.datasourceId,
        this.context.databaseName,
        tableName,
        schemaName
      );

      const columns = Array.isArray(response) ? 
        response as { name?: string; column_name?: string }[] : 
        ((response as { columns?: { name?: string; column_name?: string }[] }).columns || []);
      console.log(`Successfully fetched ${columns.length} columns for ${tableName}:`, columns);
      
      this.columnCache.set(cacheKey, columns);
      
      // Update the context with the new columns
      const existingIndex = this.context.columns.findIndex(c => c.tableName === tableName);
      if (existingIndex >= 0) {
        this.context.columns[existingIndex] = { tableName, columns };
      } else {
        this.context.columns.push({ tableName, columns });
      }

      return columns;
    } catch (error) {
      console.error(`Error fetching columns for table ${tableName}:`, error);
      
      // Cache empty result to prevent repeated failed requests
      this.columnCache.set(cacheKey, []);
      return [];
    } finally {
      this.fetchingColumns.delete(cacheKey);
    }
  }

  async provideCompletionItems(
    model: monaco.editor.ITextModel,
    position: monaco.Position
  ): Promise<monaco.languages.CompletionList> {
    const word = model.getWordUntilPosition(position);
    const line = model.getLineContent(position.lineNumber);
    const textUntilPosition = model.getValueInRange({
      startLineNumber: 1,
      startColumn: 1,
      endLineNumber: position.lineNumber,
      endColumn: position.column
    });

    const range = {
      startLineNumber: position.lineNumber,
      endLineNumber: position.lineNumber,
      startColumn: word.startColumn,
      endColumn: word.endColumn
    };

    const suggestions: monaco.languages.CompletionItem[] = [];
    const suggestionSet = new Set<string>(); // Track unique suggestions to prevent duplicates
    const wordLower = word.word.toLowerCase();

    // Analyze context to provide better suggestions
    const context = this.analyzeContext(textUntilPosition, line, position.column);

    // Extract table names from the current query
    const tablesInQuery = this.extractTableNamesFromQuery(textUntilPosition);
    
    // Fetch columns for tables that we haven't cached yet
    for (const tableName of tablesInQuery) {
      if (!this.context.columns.some(c => c.tableName === tableName)) {
        // Don't await to avoid blocking the UI, but fetch columns in background
        this.fetchColumnsForTable(tableName).catch((error: unknown) => {
          console.error(`Background column fetch failed for ${tableName}:`, error);
        });
      }
    }

    // Debug logging (can be enabled via localStorage)
    if (localStorage.getItem('monaco-debug') === 'true') {
      console.log('Monaco Autocomplete Debug:', {
        word: word.word,
        wordLower,
        context,
        textUntilPosition: textUntilPosition.substring(Math.max(0, textUntilPosition.length - 100)),
        line,
        column: position.column,
        beforeCursor: line.substring(0, position.column - 1),
        expectsTableName: context.expectsTableName,
        availableTables: this.context.tables,
        availableColumns: this.context.columns.map(c => ({ table: c.tableName, columnCount: c.columns.length })),
        tablesInQuery
      });
    }

    // Handle dot notation (table.column)
    if (this.isDotNotation(line, position.column)) {
      const tableName = this.getTableNameBeforeDot(line, position.column);
      if (tableName) {
        await this.addTableSpecificColumnSuggestions(suggestions, suggestionSet, tableName, wordLower, range);
        return { suggestions: this.sortAndLimitSuggestions(suggestions) };
      }
    }

    // Context-aware suggestion prioritization
    if (context.expectsTableName) {
      // When we expect table names (after FROM, JOIN, etc.), prioritize tables
      this.addTableSuggestions(suggestions, suggestionSet, wordLower, range, true); // high priority
      this.addSchemaSuggestions(suggestions, suggestionSet, wordLower, range);
      // Also add keywords that might be relevant
      this.addKeywordSuggestions(suggestions, suggestionSet, wordLower, range);
    } else if (context.expectsColumnName || context.isInWhere || context.afterWhere) {
      // Prioritize column suggestions when we're expecting columns or in WHERE clause
      await this.addColumnSuggestions(suggestions, suggestionSet, wordLower, range, context.fromTables);
      // Also add keywords that might be relevant in column contexts
      this.addKeywordSuggestions(suggestions, suggestionSet, wordLower, range);
    } else if (context.isTypingWord) {
      // If user is typing any word, include all suggestions but prioritize based on context
      await this.addColumnSuggestions(suggestions, suggestionSet, wordLower, range, context.fromTables);
      this.addKeywordSuggestions(suggestions, suggestionSet, wordLower, range);
      this.addSchemaSuggestions(suggestions, suggestionSet, wordLower, range);
      this.addTableSuggestions(suggestions, suggestionSet, wordLower, range, false); // normal priority
      this.addFunctionSuggestions(suggestions, suggestionSet, wordLower, range);
    } else {
      // General context - add all types of suggestions
      this.addKeywordSuggestions(suggestions, suggestionSet, wordLower, range);
      this.addSchemaSuggestions(suggestions, suggestionSet, wordLower, range);
      this.addTableSuggestions(suggestions, suggestionSet, wordLower, range, false); // normal priority
      await this.addColumnSuggestions(suggestions, suggestionSet, wordLower, range, context.fromTables);
      this.addFunctionSuggestions(suggestions, suggestionSet, wordLower, range);
    }

    // Debug the final suggestions
    if (localStorage.getItem('monaco-debug') === 'true') {
      console.log('Final suggestions:', suggestions.map(s => ({ label: s.label, sortText: s.sortText, detail: s.detail })));
    }

    return {
      suggestions: this.sortAndLimitSuggestions(suggestions)
    };
  }

  // Sort and limit suggestions for better performance and UX
  private sortAndLimitSuggestions(suggestions: monaco.languages.CompletionItem[]): monaco.languages.CompletionItem[] {
    // First, aggressively deduplicate based on label and kind
    const seenSuggestions = new Map<string, monaco.languages.CompletionItem>();
    
    for (const suggestion of suggestions) {
      const labelStr = typeof suggestion.label === 'string' ? suggestion.label : 
                       typeof suggestion.label === 'object' && suggestion.label !== null && 'label' in suggestion.label ? 
                       String((suggestion.label as {label?: unknown}).label) : 'unknown';
      const key = `${labelStr.toLowerCase()}:${suggestion.kind}`;
      if (!seenSuggestions.has(key)) {
        seenSuggestions.set(key, suggestion);
      }
    }
    
    const uniqueSuggestions = Array.from(seenSuggestions.values());
    
    // Sort by sortText (priority) and then by label alphabetically
    uniqueSuggestions.sort((a, b) => {
      const sortComparison = (a.sortText || '').localeCompare(b.sortText || '');
      if (sortComparison !== 0) return sortComparison;
      return (a.label as string).localeCompare(b.label as string);
    });
    
    // Limit to 50 suggestions for performance
    return uniqueSuggestions.slice(0, 50);
  }

  // Check if the current position is after a dot (for table.column notation)
  private isDotNotation(line: string, column: number): boolean {
    const beforeCursor = line.substring(0, column - 1);
    return /\w+\.\w*$/.test(beforeCursor);
  }

  // Extract table name before the dot
  private getTableNameBeforeDot(line: string, column: number): string | null {
    const beforeCursor = line.substring(0, column - 1);
    const match = /(\w+)\.\w*$/.exec(beforeCursor);
    return match ? match[1] : null;
  }

  // Extract table names from the entire query
  private extractTableNamesFromQuery(query: string): string[] {
    const tableNames: string[] = [];
    
    // Pattern to match table names after FROM, JOIN, UPDATE, INSERT INTO
    // Also handle table aliases and schema-qualified names
    const patterns = [
      // FROM table [alias]
      /FROM\s+(?:(\w+)\.)?(\w+)(?:\s+(?:AS\s+)?(\w+))?/gi,
      // JOIN table [alias]
      /(?:INNER\s+|LEFT\s+|RIGHT\s+|FULL\s+|CROSS\s+)?JOIN\s+(?:(\w+)\.)?(\w+)(?:\s+(?:AS\s+)?(\w+))?/gi,
      // UPDATE table
      /UPDATE\s+(?:(\w+)\.)?(\w+)/gi,
      // INSERT INTO table
      /INSERT\s+INTO\s+(?:(\w+)\.)?(\w+)/gi,
      // DELETE FROM table
      /DELETE\s+FROM\s+(?:(\w+)\.)?(\w+)/gi
    ];
    
    for (const pattern of patterns) {
      let match;
      pattern.lastIndex = 0; // Reset regex state
      while ((match = pattern.exec(query)) !== null) {
        // Extract table name (without schema prefix)
        const tableName = match[2] || match[1]; // match[2] is table name, match[1] might be schema or table
        if (tableName && !tableNames.includes(tableName)) {
          tableNames.push(tableName);
        }
      }
    }

    return tableNames;
  }

  // Add column suggestions specific to a table (for dot notation)
  private async addTableSpecificColumnSuggestions(
    suggestions: monaco.languages.CompletionItem[],
    suggestionSet: Set<string>,
    tableName: string,
    wordLower: string,
    range: monaco.IRange
  ) {
    // Try to find columns in cache first
    let tableColumns = this.context.columns.find(c => 
      c.tableName.toLowerCase() === tableName.toLowerCase()
    );

    // If not found, try to fetch them
    if (!tableColumns) {
      const columns = await this.fetchColumnsForTable(tableName);
      tableColumns = { tableName, columns };
    }

    for (const column of tableColumns.columns) {
        const columnName = column.name || column.column_name || '';
        const columnType = 'type' in column ?
          (column as { type?: string; data_type?: string }).type || (column as { type?: string; data_type?: string }).data_type : '';
        
        if (!columnName) continue;
        
        // Create unique key for deduplication
        const uniqueKey = `column:${columnName.toLowerCase()}`;
        if (suggestionSet.has(uniqueKey)) continue;
        
        const columnLower = columnName.toLowerCase();
        const startsWithWord = wordLower === '' || columnLower.startsWith(wordLower);
        const containsWord = wordLower === '' || columnLower.includes(wordLower);
        
        if (startsWithWord || containsWord) {
          suggestionSet.add(uniqueKey);
          
          suggestions.push({
            label: columnName,
            kind: monaco.languages.CompletionItemKind.Field,
            insertText: columnName,
            detail: `Column from ${tableName}${columnType ? ` (${columnType})` : ''}`,
            documentation: `Column: ${columnName}\nTable: ${tableName}${columnType ? `\nType: ${columnType}` : ''}`,
            range,
            sortText: startsWithWord ? `000${columnName}` : `001${columnName}` // High priority for dot notation
          });
        }
      }
  }

  private analyzeContext(textUntilPosition: string, currentLine: string, column: number) {
    const upperText = textUntilPosition.toUpperCase();
    const beforeCursor = currentLine.substring(0, column - 1).toUpperCase();
    const entireLineBefore = currentLine.substring(0, column - 1);

    // Extract table names from FROM and JOIN clauses using improved extraction
    const fromTables = this.extractTableNamesFromQuery(textUntilPosition);

    // Enhanced context detection - more aggressive WHERE clause detection
    const isAfterSelect = /\bSELECT\b(?!\s*\*\s*FROM)[^;]*$/i.test(upperText);
    const isAfterWhere = /\bWHERE\b[^;]*$/i.test(upperText);
    const isAfterGroupBy = /\bGROUP\s+BY\b[^;]*$/i.test(upperText);
    const isAfterOrderBy = /\bORDER\s+BY\b[^;]*$/i.test(upperText);
    const isAfterHaving = /\bHAVING\b[^;]*$/i.test(upperText);
    
    // Better partial word detection - check if we're currently typing a word
    const currentWordMatch = /[a-zA-Z_][a-zA-Z0-9_]*$/.exec(entireLineBefore);
    const currentWord = currentWordMatch ? currentWordMatch[0] : '';
    const isTypingWord = currentWord.length > 0;
    
    // More comprehensive detection of when column names are expected
    const expectsColumnName = (
      isAfterSelect ||
      isAfterWhere ||
      isAfterGroupBy ||
      isAfterOrderBy ||
      isAfterHaving ||
      // After comma in SELECT, GROUP BY, ORDER BY
      /,\s*$/i.test(beforeCursor) ||
      // After operators that expect column names
      /\b(AND|OR|=|!=|<>|<|>|<=|>=|LIKE|IN|NOT\s+IN|BETWEEN)\s*$/i.test(beforeCursor) ||
      // After opening parenthesis in functions or conditions
      /\(\s*$/i.test(beforeCursor)
    );

    // Check if we expect table names - improved detection
    const expectsTableName = (
      /\b(FROM|JOIN|UPDATE|INSERT\s+INTO|DELETE\s+FROM)\s*$/i.test(beforeCursor) ||
      /\b(FROM|JOIN|UPDATE|INSERT\s+INTO|DELETE\s+FROM)\s+\w*$/i.test(beforeCursor) ||
      // Also detect when we're typing after these keywords
      /\b(INNER\s+JOIN|LEFT\s+JOIN|RIGHT\s+JOIN|FULL\s+JOIN|CROSS\s+JOIN)\s*$/i.test(beforeCursor) ||
      /\b(INNER\s+JOIN|LEFT\s+JOIN|RIGHT\s+JOIN|FULL\s+JOIN|CROSS\s+JOIN)\s+\w*$/i.test(beforeCursor)
    );

    // More specific WHERE clause detection
    const isInWhereClause = isAfterWhere || (
      /\bWHERE\b/i.test(upperText) && 
      !/\b(GROUP\s+BY|ORDER\s+BY|HAVING|LIMIT|UNION|;)\b/i.test(upperText.substring(upperText.lastIndexOf('WHERE')))
    );

    return {
      expectsTableName,
      expectsColumnName,
      fromTables,
      isInSelect: /\bSELECT\b/i.test(upperText),
      isInWhere: isInWhereClause,
      isInInsert: /\bINSERT\s+INTO\b/i.test(upperText),
      isInUpdate: /\bUPDATE\b/i.test(upperText),
      currentWord,
      isTypingWord,
      beforeCursor: entireLineBefore, // Add for debugging
      afterWhere: isAfterWhere
    };
  }

  private addKeywordSuggestions(
    suggestions: monaco.languages.CompletionItem[],
    suggestionSet: Set<string>,
    wordLower: string,
    range: monaco.IRange
  ) {
    for (const keyword of SQL_KEYWORDS) {
      const keywordLower = keyword.toLowerCase();
      const uniqueKey = `keyword:${keywordLower}`;
      
      if (suggestionSet.has(uniqueKey)) continue;
      
      const startsWithWord = wordLower === '' || keywordLower.startsWith(wordLower);
      const containsWord = wordLower === '' || keywordLower.includes(wordLower);
      
      if (startsWithWord || containsWord) {
        suggestionSet.add(uniqueKey);
        
        let insertText = keyword;
        let detail = 'SQL Keyword';
        
        // Add contextual snippets for complex keywords
        if (keyword === 'SELECT' && wordLower === 'sel') {
          insertText = `SELECT \${1:columns} FROM \${2:table}`;
          detail = 'SELECT statement';
        } else if (keyword === 'INSERT INTO' && wordLower === 'ins') {
          insertText = `INSERT INTO \${1:table} (\${2:columns}) VALUES (\${3:values})`;
          detail = 'INSERT statement';
        } else if (keyword === 'UPDATE' && wordLower === 'upd') {
          insertText = `UPDATE \${1:table} SET \${2:column} = \${3:value} WHERE \${4:condition}`;
          detail = 'UPDATE statement';
        }

        const sortText = startsWithWord ? `100${keyword}` : `200${keyword}`;
        
        suggestions.push({
          label: keyword,
          kind: monaco.languages.CompletionItemKind.Keyword,
          insertText,
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          detail,
          range,
          sortText
        });
      }
    }
  }

  private addTableSuggestions(
    suggestions: monaco.languages.CompletionItem[],
    suggestionSet: Set<string>,
    wordLower: string,
    range: monaco.IRange,
    highPriority = false
  ) {
    for (const table of this.context.tables) {
      const tableLower = table.toLowerCase();
      const uniqueKey = `table:${tableLower}`;
      
      if (suggestionSet.has(uniqueKey)) continue;
      
      const startsWithWord = wordLower === '' || tableLower.startsWith(wordLower);
      const containsWord = wordLower === '' || tableLower.includes(wordLower);
      
      if (startsWithWord || containsWord) {
        suggestionSet.add(uniqueKey);
        
        // High priority when we expect table names (after FROM, JOIN, etc.)
        let sortText: string;
        if (highPriority) {
          sortText = startsWithWord ? `020${table}` : `030${table}`; // Higher than columns
        } else {
          sortText = startsWithWord ? `300${table}` : `400${table}`; // Normal priority
        }
        
        suggestions.push({
          label: table,
          kind: monaco.languages.CompletionItemKind.Class,
          insertText: table,
          detail: 'Table',
          documentation: `Table: ${table}`,
          range,
          sortText
        });
      }
    }
  }

  private addSchemaSuggestions(
    suggestions: monaco.languages.CompletionItem[],
    suggestionSet: Set<string>,
    wordLower: string,
    range: monaco.IRange
  ) {
    for (const schema of this.context.schemas) {
      if (!schema) continue;
      
      const schemaLower = schema.toLowerCase();
      const uniqueKey = `schema:${schemaLower}`;
      
      if (suggestionSet.has(uniqueKey)) continue;
      
      const startsWithWord = wordLower === '' || schemaLower.startsWith(wordLower);
      const containsWord = wordLower === '' || schemaLower.includes(wordLower);
      
      if (startsWithWord || containsWord) {
        suggestionSet.add(uniqueKey);
        
        const sortText = startsWithWord ? `050${schema}` : `150${schema}`;
        
        suggestions.push({
          label: schema,
          kind: monaco.languages.CompletionItemKind.Module,
          insertText: schema,
          detail: 'Schema/Database',
          documentation: `Schema or Database: ${schema}`,
          range,
          sortText
        });
      }
    }
  }

  private async addColumnSuggestions(
    suggestions: monaco.languages.CompletionItem[],
    suggestionSet: Set<string>,
    wordLower: string,
    range: monaco.IRange,
    fromTables: string[]
  ) {
    // Create a map for better performance when checking priority tables
    const priorityTableSet = new Set(fromTables.map(t => t.toLowerCase()));
    
    // For tables in the FROM clause that don't have columns yet, try to fetch them
    for (const tableName of fromTables) {
      if (!this.context.columns.some(c => c.tableName.toLowerCase() === tableName.toLowerCase())) {
        try {
          await this.fetchColumnsForTable(tableName);
        } catch (error) {
          console.error(`Failed to fetch columns for table ${tableName}:`, error);
        }
      }
    }
    
    for (const { tableName, columns } of this.context.columns) {
      // Prioritize columns from tables in FROM clause
      const isPriorityTable = priorityTableSet.has(tableName.toLowerCase());
      
      for (const column of columns) {
        const columnName = column.name || column.column_name || '';
        const columnType = 'type' in column ?
          (column as { type?: string; data_type?: string }).type || (column as { type?: string; data_type?: string }).data_type : '';
        
        if (!columnName) continue;
        
        // Enhanced matching: check if column name starts with or contains the typed word
        const columnLower = columnName.toLowerCase();
        const startsWithWord = wordLower === '' || columnLower.startsWith(wordLower);
        const containsWord = wordLower === '' || columnLower.includes(wordLower);
        
        if (startsWithWord || containsWord) {
          // Regular column suggestion
          const columnKey = `column:${columnLower}`;
          if (!suggestionSet.has(columnKey)) {
            suggestionSet.add(columnKey);
            
            // Priority scoring for better ordering
            let sortText: string;
            if (isPriorityTable) {
              sortText = startsWithWord ? `010${columnName}` : `011${columnName}`;
            } else {
              sortText = startsWithWord ? `012${columnName}` : `013${columnName}`;
            }
            
            suggestions.push({
              label: columnName,
              kind: monaco.languages.CompletionItemKind.Field,
              insertText: columnName,
              detail: `Column from ${tableName}${columnType ? ` (${columnType})` : ''}`,
              documentation: `Column: ${columnName}\nTable: ${tableName}${columnType ? `\nType: ${columnType}` : ''}`,
              range,
              sortText
            });
          }

          // Qualified column suggestion (only if multiple tables and makes sense)
          if (fromTables.length > 1) {
            const qualifiedName = `${tableName}.${columnName}`;
            const qualifiedLower = qualifiedName.toLowerCase();
            const qualifiedKey = `qualified:${qualifiedLower}`;
            
            if (!suggestionSet.has(qualifiedKey) && 
                (qualifiedLower.includes(wordLower) || wordLower === '')) {
              suggestionSet.add(qualifiedKey);
              
              let qualifiedSortText: string;
              if (isPriorityTable) {
                qualifiedSortText = startsWithWord ? `014${qualifiedName}` : `015${qualifiedName}`;
              } else {
                qualifiedSortText = startsWithWord ? `016${qualifiedName}` : `017${qualifiedName}`;
              }
              
              suggestions.push({
                label: qualifiedName,
                kind: monaco.languages.CompletionItemKind.Field,
                insertText: qualifiedName,
                detail: `Qualified column from ${tableName}${columnType ? ` (${columnType})` : ''}`,
                documentation: `Qualified column: ${tableName}.${columnName}${columnType ? `\nType: ${columnType}` : ''}`,
                range,
                sortText: qualifiedSortText
              });
            }
          }
        }
      }
    }
  }

  private addFunctionSuggestions(
    suggestions: monaco.languages.CompletionItem[],
    suggestionSet: Set<string>,
    wordLower: string,
    range: monaco.IRange
  ) {
    // Built-in SQL functions
    const sqlFunctions = [
      { name: 'COUNT', params: 'column', desc: 'Count rows' },
      { name: 'SUM', params: 'column', desc: 'Sum values' },
      { name: 'AVG', params: 'column', desc: 'Average values' },
      { name: 'MIN', params: 'column', desc: 'Minimum value' },
      { name: 'MAX', params: 'column', desc: 'Maximum value' },
      { name: 'CONCAT', params: 'str1, str2', desc: 'Concatenate strings' },
      { name: 'SUBSTRING', params: 'string, start, length', desc: 'Extract substring' },
      { name: 'LENGTH', params: 'string', desc: 'String length' },
      { name: 'UPPER', params: 'string', desc: 'Convert to uppercase' },
      { name: 'LOWER', params: 'string', desc: 'Convert to lowercase' },
      { name: 'TRIM', params: 'string', desc: 'Remove leading/trailing spaces' },
      { name: 'NOW', params: '', desc: 'Current timestamp' },
      { name: 'CURRENT_DATE', params: '', desc: 'Current date' },
      { name: 'CURRENT_TIME', params: '', desc: 'Current time' }
    ];

    // Add built-in SQL functions
    for (const func of sqlFunctions) {
      const funcNameLower = func.name.toLowerCase();
      const uniqueKey = `function:${funcNameLower}`;
      
      if (suggestionSet.has(uniqueKey)) continue;
      
      const startsWithWord = wordLower === '' || funcNameLower.startsWith(wordLower);
      const containsWord = wordLower === '' || funcNameLower.includes(wordLower);
      
      if (startsWithWord || containsWord) {
        suggestionSet.add(uniqueKey);
        
        const insertText = func.params ? `${func.name}(${func.params})` : `${func.name}()`;
        const sortText = startsWithWord ? `500${func.name}` : `600${func.name}`;
        
        suggestions.push({
          label: func.name,
          kind: monaco.languages.CompletionItemKind.Function,
          insertText,
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          detail: `Built-in: ${func.desc}`,
          documentation: `Built-in SQL function\n${func.desc}\nUsage: ${insertText}`,
          range,
          sortText
        });
      }
    }

    // Add database-specific functions
    for (const func of this.context.functions) {
      // Handle both string and object function data
      const functionName = typeof func === 'string' ? func : func.name || '';
      const functionObj = typeof func === 'object' ? func : null;
      
      if (!functionName) continue;
      
      const functionNameLower = functionName.toLowerCase();
      const uniqueKey = `dbfunction:${functionNameLower}`;
      
      if (suggestionSet.has(uniqueKey)) continue;
      
      const startsWithWord = wordLower === '' || functionNameLower.startsWith(wordLower);
      const containsWord = wordLower === '' || functionNameLower.includes(wordLower);
      
      if (startsWithWord || containsWord) {
        suggestionSet.add(uniqueKey);
        
        // Extract parameter information if available
        let insertText = `${functionName}()`;
        let parameterInfo = '';
        let documentation = `Database-specific function: ${functionName}`;
        
        if (functionObj && functionObj.parameters && Array.isArray(functionObj.parameters)) {
          const params = functionObj.parameters
            .filter(p => p.mode !== 'OUT') // Exclude OUT parameters from input
            .map(p => {
              const paramName = p.name || 'param';
              const paramType = p.type ? ` (${p.type})` : '';
              return `${paramName}${paramType}`;
            });
          
          if (params.length > 0) {
            insertText = `${functionName}(${params.join(', ')})`;
            parameterInfo = ` - Parameters: ${params.join(', ')}`;
          }
        }
        
        // Add return type and other metadata
        if (functionObj) {
          if (functionObj.returnType) {
            documentation += `\nReturn type: ${functionObj.returnType}`;
          }
          if (functionObj.language) {
            documentation += `\nLanguage: ${functionObj.language}`;
          }
          if (functionObj.schema) {
            documentation += `\nSchema: ${functionObj.schema}`;
          }
          if (parameterInfo) {
            documentation += `\n${parameterInfo}`;
          }
        }
        
        const sortText = startsWithWord ? `400${functionName}` : `450${functionName}`;
        
        suggestions.push({
          label: functionName,
          kind: monaco.languages.CompletionItemKind.Function,
          insertText,
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          detail: `Database function${functionObj?.returnType ? ` → ${functionObj.returnType}` : ''}`,
          documentation,
          range,
          sortText
        });
      }
    }
  }

  // Clear the column cache (useful when datasource/database changes)
  public clearColumnCache() {
    this.columnCache.clear();
    this.fetchingColumns.clear();
  }

  // Get current context for debugging
  public getContext() {
    return {
      ...this.context,
      cacheSize: this.columnCache.size,
      fetchingCount: this.fetchingColumns.size,
      cachedTables: Array.from(this.columnCache.keys())
    };
  }
}
